import { Button, DraggableWindowHeader, log } from "@hoylu/client-common";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { cancelDashboardOption } from "../../state/workspaces/workspaces.actions";
import { useI18n } from "../../utils/hooks/use.i18n";
import Styles from "./AnnouncementsDialog.module.css";
import { Modal } from "./Modal";

const announcementsUrl = "https://www.hoylu.com/announcements/";
const announcementsFeedUrl =
  "https://www.hoylu.com/category/product-details/feed/";

export const AnnouncementsDialog = () => {
  const [doShowAgainButton, setDoShowAgainButton] = useState(false);
  const t = useI18n("ANNOUNCEMENTS_DIALOG.");
  const tClose = useI18n("CLOSE_BUTTON.");
  const dispatch = useDispatch();

  const handleClose = async (markAsRead: boolean) => {
    dispatch(cancelDashboardOption());
    if (markAsRead) {
      await markAnnouncementsAsRead();
    }
  };

  useEffect(() => {
    const checkForNewAnnouncements = async () => {
      try {
        const hasNew = await hasNewAnnouncements();
        setDoShowAgainButton(hasNew);
      } catch (error) {
        log.error("Error checking for new announcements", error);
      }
    };
    checkForNewAnnouncements();
  }, []);

  return (
    <Modal>
      <DraggableWindowHeader
        close={tClose("CLOSE")}
        onClose={() => handleClose(true)}
        title={t("TITLE")}
      />
      <div className={Styles.whatsNewDialog}>
        <iframe
          className={Styles.iframe}
          src={announcementsUrl}
          title="Announcements"
        />
      </div>
      <div className={Styles.footer}>
        {doShowAgainButton && (
          <Button
            onClick={() => handleClose(false)}
            toolTip={t("REMIND_ME_LATER")}
            type="negative"
          >
            {t("REMIND_ME_LATER")}
          </Button>
        )}
        <Button
          onClick={() => handleClose(true)}
          toolTip={t("OK")}
          type="primary"
        >
          {t("OK")}
        </Button>
      </div>
    </Modal>
  );
};

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const CACHED_ANNOUNCEMENT_DATE_KEY = "cachedAnnouncementDate";
const CACHED_ANNOUNCEMENT_FETCH_TIME_KEY = "cachedAnnouncementFetchTime";
const LAST_ANNOUNCEMENT_VISIT_KEY = "lastAnnouncementsVisit";

// Check if there are any new announcements from in our RSS-XML feed by checking the lastBuildDate
export async function hasNewAnnouncements() {
  const lastBuild = await getLastAnnouncementDateFromFeed();
  if (!lastBuild) {
    throw new Error("Could not get last announcement date from feed");
  }
  const lastVisit = localStorage.getItem(LAST_ANNOUNCEMENT_VISIT_KEY);
  return !lastVisit || new Date(lastVisit) < lastBuild;
}

// Get the lastBuildDate from our RSS-XML feed, cache result for 1 hour in sessionStorage
async function getLastAnnouncementDateFromFeed() {
  const now = Date.now();
  const cachedDateStr = sessionStorage.getItem(CACHED_ANNOUNCEMENT_DATE_KEY);
  const cachedFetchTimeStr = sessionStorage.getItem(
    CACHED_ANNOUNCEMENT_FETCH_TIME_KEY,
  );

  if (cachedDateStr && cachedFetchTimeStr) {
    const cachedDate = new Date(cachedDateStr);
    const cachedFetchTime = Number(cachedFetchTimeStr);
    if (now - cachedFetchTime < CACHE_DURATION) {
      return cachedDate;
    }
  }

  sessionStorage.setItem(CACHED_ANNOUNCEMENT_FETCH_TIME_KEY, now.toString());
  const response = await fetch(announcementsFeedUrl);
  const text = await response.text();
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(text, "text/xml");
  const lastBuildDate =
    xmlDoc.getElementsByTagName("lastBuildDate")[0]?.textContent;
  if (!lastBuildDate) {
    if (!cachedDateStr)
      throw new Error("No lastBuildDate in feed and no cached date");
    return new Date(cachedDateStr); // fallback to cached date if available
  }
  sessionStorage.setItem(
    CACHED_ANNOUNCEMENT_DATE_KEY,
    new Date(lastBuildDate).toISOString(),
  );
  return new Date(lastBuildDate);
}

async function markAnnouncementsAsRead() {
  try {
    const lastBuild = await getLastAnnouncementDateFromFeed();
    localStorage.setItem(LAST_ANNOUNCEMENT_VISIT_KEY, lastBuild.toISOString());
  } catch (error) {
    log.error("Error marking announcements as read:", error);
    // If we can't get the last announcement date, just set to now to avoid showing again
    localStorage.setItem(LAST_ANNOUNCEMENT_VISIT_KEY, new Date().toISOString());
  }
}
